import { useEffect, useState } from "react";
import { fetchPeople, flyerStatusText, addMedia, blankMedia, deleteMedia, fetchShow, mediaTypeLabel, moveItem, reorderMedia, setAttendees, shareText, toggle, MEDIA_TYPES, type MediaDraft } from "../../lib/showSheet";
import { showDetailReel } from "../../lib/flyerViewer";
import { fLong, plural } from "../../lib/format";
import { fetchSetlists, makePlaylist, type Playlist, type Setlist } from "../../lib/playlist";
import type { Person, ShowDetail } from "../../lib/types";
import { useApp } from "../appState";
import { Flyer } from "../Flyer";
import { FlyerSource } from "../FlyerTools";
import { searchUrl, setShowFlyer, storeFlyer } from "../../lib/flyer";
import { Sheet } from "../Sheet";

/** Playlist dialog: a private Spotify playlist per band, from its setlist.fm songs. */
function Playlists({ show, onClose }: { show: ShowDetail; onClose: () => void }) {
  const [sets, setSets] = useState<Setlist[] | null>(null);
  const [made, setMade] = useState<Playlist[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    fetchSetlists(show.id).then((x) => live && setSets(x), (e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [show.id]);
  const make = async (list: Setlist[]) => {
    setError(null);
    for (const s of list) {
      setBusy(s.name);
      try {
        const p = await makePlaylist(show.id, s.band_id);
        setMade((m) => [...m.filter((x) => x.band_id !== p.band_id), p]);
      } catch (e) {
        setError((e as Error).message);
        break;
      }
    }
    setBusy(null);
  };
  return (
    <section className="playlists">
      <div className="sect-h">
        <p className="eyebrow">Spotify playlist</p>
        <button type="button" className="meta link" onClick={onClose}>Close</button>
      </div>
      {!sets && !error && <p className="loading">Checking setlists…</p>}
      {sets && !sets.length && <p className="muted" style={{ margin: 0 }}>None of the setlist pages have songs yet.</p>}
      {sets && sets.length > 0 && (
        <div className="sheet-acts">
          {sets.map((s) => (
            <button type="button" key={s.band_id} className={`btn sm ${sets.length === 1 ? "primary" : "ghost"}`} disabled={Boolean(busy)} onClick={() => make([s])}>
              {s.name} ({plural(s.songs.length, "song")})
            </button>
          ))}
          {sets.length > 1 && (
            <button type="button" className="btn sm primary" disabled={Boolean(busy)} onClick={() => make(sets)}>All sets</button>
          )}
        </div>
      )}
      {busy && <p className="loading">Making the {busy} playlist…</p>}
      {made.map((p) => (
        <div className="made" key={p.band_id}>
          <span><b>{p.name}</b>: {p.matched} of {plural(p.total, "song")}</span>
          <a className="btn sm" href={p.url} target="_blank" rel="noopener noreferrer">Open {p.name} in Spotify</a>
          {p.missing.length > 0 && <span className="faint">Missing: {p.missing.join(", ")}</span>}
        </div>
      ))}
      {error && <p className="error">{error}</p>}
    </section>
  );
}

function WentWith({ show }: { show: ShowDetail }) {
  const { changed, toast } = useApp();
  const [tagging, setTagging] = useState(false);
  const [people, setPeople] = useState<Person[] | null>(null);
  const [ids, setIds] = useState(show.people.map((p) => p.id));
  const [solo, setSolo] = useState(show.solo);

  // Follow the saved attendees after a refetch, but never mid-tagging: a media
  // write elsewhere on the sheet must not reset picks not yet saved. (2026-09-11 gate)
  useEffect(() => {
    if (tagging) return;
    setIds(show.people.map((p) => p.id));
    setSolo(show.solo);
  }, [show, tagging]);

  const start = () => {
    setTagging(true);
    if (!people) fetchPeople().then(setPeople, (e: Error) => toast(e.message));
  };
  const done = () =>
    setAttendees(show.id, ids, solo).then(() => {
      setTagging(false);
      changed("Saved who went");
    }, (e: Error) => toast(e.message));

  // Who went is logged, but kept quiet: names only, after the bill and media (Matt, 2026-09-11).
  return (
    <section>
      {tagging ? (
        <>
          <div className="sect-h">
            <p className="eyebrow">Went with</p>
            <button type="button" className="meta link" onClick={done}>Done</button>
          </div>
          <div className="ptag plain">
            {(people ?? []).map((p) => (
              <button type="button" key={p.id} aria-pressed={!solo && ids.includes(p.id)} onClick={() => { setSolo(false); setIds(toggle(ids, p.id)); }}>
                {p.name}
              </button>
            ))}
            <button type="button" aria-pressed={solo} onClick={() => setSolo(!solo)}>
              Solo
            </button>
            {!people && <span className="loading">Loading people…</span>}
          </div>
        </>
      ) : (
        <p className="quiet">
          {show.people.length ? <>Went with <b>{show.people.map((p) => p.name).join(", ")}</b></> : show.solo ? "Went solo" : "Nobody tagged"}
          {" · "}
          <button type="button" className="link" onClick={start}>{show.people.length || show.solo ? "Edit" : "Tag people"}</button>
        </p>
      )}
    </section>
  );
}

function Media({ show }: { show: ShowDetail }) {
  const { changed, toast } = useApp();
  const [draft, setDraft] = useState<MediaDraft>(blankMedia());
  const fail = (e: Error) => toast(e.message);
  const move = (i: number, dir: -1 | 1) => reorderMedia(show.id, moveItem(show.media, i, dir)).then(() => changed(), fail);
  const add = () =>
    addMedia(show.id, draft).then(() => {
      setDraft(blankMedia());
      changed("Link added");
    }, fail);
  return (
    <section>
      <div className="sect-h">
        <p className="eyebrow">Media</p>
        <span className="meta">{show.media.length || "none yet"}</span>
      </div>
      {show.media.map((m, i) => (
        <div className="mrow" key={m.id}>
          <span className="fmt">{m.media_format === "audio" ? "♪" : "▶"}</span>
          <span style={{ minWidth: 0 }}>
            <a className="mt link" href={m.url} target="_blank" rel="noopener noreferrer">
              {m.title || m.band_name || mediaTypeLabel(m.media_type)}
            </a>
            <br />
            <span className="ms">{[m.band_name ?? "Whole show", mediaTypeLabel(m.media_type), m.media_format].join(" · ")}</span>
          </span>
          <span className="ord">
            <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
            <button type="button" aria-label="Move down" disabled={i === show.media.length - 1} onClick={() => move(i, 1)}>↓</button>
            <button type="button" aria-label="Remove" onClick={() => deleteMedia(m.id).then(() => changed("Link removed"), fail)}>✕</button>
          </span>
        </div>
      ))}
      <div className="form" style={{ marginTop: 10 }}>
        <div className="billed">
          <div className="add">
            <input value={draft.url} placeholder="Paste a YouTube, Archive or Bandcamp link" aria-label="Media link" onChange={(e) => setDraft({ ...draft, url: e.target.value })} />
            <button type="button" className="btn sm" disabled={!draft.url.trim()} onClick={add}>
              Add
            </button>
          </div>
        </div>
        {draft.url.trim() && (
          <div className="two">
            <label className="f">
              <span>Band</span>
              <select value={draft.band_id ?? ""} onChange={(e) => setDraft({ ...draft, band_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">Whole show</option>
                {show.bill.map((b) => (
                  <option key={b.band_id} value={b.band_id}>{b.name}</option>
                ))}
              </select>
            </label>
            <label className="f">
              <span>Kind</span>
              <select value={`${draft.media_type}|${draft.media_format}`} onChange={(e) => { const [t, f] = e.target.value.split("|"); setDraft({ ...draft, media_type: t, media_format: f }); }}>
                {MEDIA_TYPES.flatMap(([v, l]) => [
                  <option key={`${v}v`} value={`${v}|video`}>{l}, video</option>,
                  <option key={`${v}a`} value={`${v}|audio`}>{l}, audio</option>,
                ])}
              </select>
            </label>
          </div>
        )}
      </div>
    </section>
  );
}

export function ShowSheet({ id }: { id: number }) {
  const { open, close, zoom, version, toast, changed } = useApp();
  const [show, setShow] = useState<ShowDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [playlist, setPlaylist] = useState(false);

  useEffect(() => {
    let live = true;
    fetchShow(id)
      .then((s) => live && setShow(s))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [id, version]);

  const copy = () =>
    show && navigator.clipboard.writeText(shareText(show, fLong(show.date))).then(() => toast("Copied"), () => toast("Could not copy"));

  return (
    <Sheet label={show ? `Show #${show.number}` : "Show"} onClose={close}>
      {error && <p className="error">{error}</p>}
      {!show && !error && <p className="loading">Loading…</p>}
      {show && (
        <>
          <div className="sh-top">
            <div className="sh-flyer">
              <Flyer url={show.image_url} alt={`Flyer for show #${show.number}`} natural
                text={{ date: show.date, bands: show.bill.map((b) => b.name), venue: show.venue.name }}
                onZoom={() => zoom(showDetailReel(show))} />
              {show.image_url && !!show.alt_flyers?.length && (
                <span className="alt-count" title={plural(show.alt_flyers.length, "alternate flyer")}>+{show.alt_flyers.length}</span>
              )}
            </div>
            <div>
              <div className="sh-num">#{show.number}</div>
              <div className="sh-date">{fLong(show.date)}</div>
              {show.event && <span className="pill">{show.event}</span>}
            </div>
          </div>
          {!show.image_url && (
            <div className="status-box">
              <span style={{ flex: 1 }}>{flyerStatusText(show.flyer_status)}</span>
              {!adding && <button type="button" className="btn sm" onClick={() => setAdding(true)}>Add flyer</button>}
            </div>
          )}
          {!show.image_url && adding && (
            <div className="add-flyer">
              <FlyerSource primary busyText="Saving the flyer…"
                onPick={async (p) => {
                  const url = await storeFlyer(p, show.bill[0]?.name ?? "", show.date);
                  await setShowFlyer(show.id, url);
                  setAdding(false);
                  changed("Flyer added");
                }} />
              <div className="sheet-acts">
                <a className="btn ghost sm" target="_blank" rel="noopener noreferrer"
                  href={searchUrl({ event: show.event ?? "", bands: show.bill.map((b) => b.name), venue: show.venue.name, date: show.date })}>
                  Search Google Images
                </a>
                <button type="button" className="btn ghost sm" onClick={() => setAdding(false)}>Cancel</button>
              </div>
            </div>
          )}
          <section>
            <p className="eyebrow" style={{ marginBottom: 4 }}>Bill</p>
            <ol className="billlist">
              {show.bill.map((b) => (
                <li key={b.band_id}>
                  <button type="button" className="link" onClick={() => open("band", b.primary_id ?? b.band_id)}>
                    {b.name}
                  </button>
                  {b.primary_name && <span className="faint" style={{ fontSize: 12 }}>grouped under {b.primary_name}</span>}
                  {b.setlist_url && (
                    <a className="sl" href={b.setlist_url} target="_blank" rel="noopener noreferrer">setlist</a>
                  )}
                </li>
              ))}
            </ol>
          </section>
          <dl className="kv">
            <dt>Venue</dt>
            <dd>
              <button type="button" className="link" onClick={() => open("venue", show.venue.id)}>
                {show.venue.name}
              </button>
              <br />
              <span className="faint">
                {show.venue.location}
                {show.venue.closed ? " · closed" : ""}
              </span>
            </dd>
          </dl>
          <Media show={show} />
          <WentWith show={show} />
          <div className="sheet-acts">
            <button type="button" className="btn ghost" onClick={() => open("edit", show.id)}>
              Edit show
            </button>
            <button type="button" className="btn ghost" onClick={copy}>
              Copy share text
            </button>
            {show.bill.some((b) => b.setlist_url) && !playlist && (
              <button type="button" className="btn ghost" onClick={() => setPlaylist(true)}>
                Spotify playlist
              </button>
            )}
          </div>
          {playlist && <Playlists show={show} onClose={() => setPlaylist(false)} />}
        </>
      )}
    </Sheet>
  );
}
